/**
 * Self-contained reader for paramount's shareable-URL filters (`assetbank_shareable_url`).
 *
 * Decodes the page's query string into the `{ filters, searchTerms }` the dropdown sends on
 * `search/spotlight/`. Like its sibling `statelessFiltersFromSessionStorage.ts` (the only import),
 * it has NO runtime dependencies.
 *
 * It mirrors paramount's split-on path in `src/assets/overview/`: `utils/urlFilters.ts`
 * `decodeFilters` + `utils/fieldCodecs.ts` (query string -> filter state), then
 * `apis/utils.api.ts` `getFiltersParamsFromUrl` + `buildSearchTermQueryClauses` (-> request). Only
 * the metaproperty clauses are id-based; tags, advanced, colour and search terms are byte-identical
 * to the session path, so they go through the session mapper. Deliberately not mirrored: legacy URL
 * aliases, similarity (`similar*`) and `sort` / `view` / `savedFilterId` / `mediaId`. Filters it
 * can't turn into a clause are dropped and reported through `onDropped`. `__tests__/statelessFiltersUrlGoldenFixture.test.ts` enforces
 * the sync against a fixture shared byte-for-byte with paramount.
 */

import {
	buildDateRangeValue,
	FilteringOptionType,
	SearchOptionType,
	mapFilteringOptionsToFiltersParam,
	type FilteringOption,
	type FiltersParameterForPostRequest,
	type SearchOperator,
	type SelectedSearchOption,
} from './statelessFiltersFromSessionStorage';

// ---------------------------------------------------------------------------
//  Types
// ---------------------------------------------------------------------------

export type UrlAdvancedSingleField = 'type' | 'orientation' | 'userCreated';
export type UrlAdvancedRangeField =
	| 'createdAt'
	| 'publishedAt'
	| 'archivedAt'
	| 'resolution'
	| 'duration'
	| 'dpi';
export type UrlAdvancedBooleanField =
	| 'archive'
	| 'watermark'
	| 'active'
	| 'downloadable'
	| 'keyVisual'
	| 'hasUsage'
	| 'public';

export type UrlRangeFilter = {
	field: UrlAdvancedRangeField;
	preset?: string;
	from?: string | null;
	to?: string | null;
};

// Select, exclude and date groups carry ColdFusion-form ids (as paramount decodes them); bulk groups
// are keyed by the metaproperty DB name.
export type UrlMetapropertyGroup =
	| { mpId: string; optionIds: string[] }
	| { mpId: string; exclude: true }
	| { mpId: string; dateRange: { from: string; to: string } }
	| { fieldName: string; bulkTerms: string[] };

export type UrlFilters = {
	metaproperties: UrlMetapropertyGroup[];
	manualTags: string[];
	autoTags: string[];
	searchTerms: string[];
	titleTerms: string[];
	descriptionTerms: string[];
	stickySearchTerm: string | null;
	stickyTitle: string | null;
	stickyDescription: string | null;
	advancedSingleFilters: Array<{ field: UrlAdvancedSingleField; value: string }>;
	advancedRangeFilters: UrlRangeFilter[];
	advancedBooleanFilters: Array<{ field: UrlAdvancedBooleanField; value: boolean }>;
	color: string | null;
};

// ---------------------------------------------------------------------------
//  Constants
// ---------------------------------------------------------------------------

const METAPROPERTY_KEY_PREFIX = 'mp_';
const BULK_SENTINEL_PREFIX = 'bulk:';
const DATE_SENTINEL_PREFIX = 'date:';

const DATE_RANGE_PRESETS = ['today', 'lastday', 'lastweek', 'next7days', 'next31days'];

const RANGE_PRESETS_BY_FIELD: Record<UrlAdvancedRangeField, string[]> = {
	createdAt: DATE_RANGE_PRESETS,
	publishedAt: DATE_RANGE_PRESETS,
	archivedAt: DATE_RANGE_PRESETS,
	resolution: ['small', 'medium', 'large'],
	duration: ['short', 'medium', 'long'],
	dpi: [],
};

const HEX6_CHARS_REGEX = /^[0-9a-f]{6}$/i;
const HEX3_CHARS_REGEX = /^[0-9a-f]{3}$/i;
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// ---------------------------------------------------------------------------
//  Ids
// ---------------------------------------------------------------------------

const isUUID = (id: string): boolean => UUID_REGEX.test(id);

// ColdFusion form: `0879CD39-F386-4ABA-A8FC2D6B9D9C480E`.
const isColdfusionID = (id: string): boolean =>
	id.length === 35 && (id.match(/-/g) || []).length === 3;

const uuidToColdfusionID = (uuid: string): string =>
	`${uuid.substring(0, 23)}${uuid.substring(24)}`.toUpperCase();

const coldfusionIDtoUUID = (coldfusionId: string): string =>
	`${coldfusionId.substring(0, 23)}-${coldfusionId.substring(23)}`.toLowerCase();

const metapropertyIdFromUrl = (id: string): string => (isUUID(id) ? uuidToColdfusionID(id) : id);

/** The id as the backend's `metapropertyid_` clauses expect it: a lowercase UUID, anything else as-is. */
export const metapropertyIdToUUID = (id: string): string =>
	isColdfusionID(id) ? coldfusionIDtoUUID(id) : id;

// ---------------------------------------------------------------------------
//  Query string
// ---------------------------------------------------------------------------

const TERM_LIST_KEYS: ReadonlySet<string> = new Set(['searchTerm', 'title', 'description']);

const safeDecodeURIComponent = (raw: string): string | null => {
	try {
		return decodeURIComponent(raw);
	} catch {
		return null;
	}
};

const decodeRawValue = (raw: string): string | null =>
	safeDecodeURIComponent(raw.replace(/\+/g, ' '));

const isTermListEntry = (key: string, rawValue: string): boolean =>
	TERM_LIST_KEYS.has(key) ||
	(key.startsWith(METAPROPERTY_KEY_PREFIX) && rawValue.startsWith(BULK_SENTINEL_PREFIX));

// Term lists are split before decoding, so a `,` or `|` inside a term (written `%2C`/`%7C`) stays in it.
const expandTermList = (key: string, rawValue: string): Array<[string, string]> => {
	const prefix = rawValue.startsWith(BULK_SENTINEL_PREFIX) ? BULK_SENTINEL_PREFIX : '';

	return rawValue
		.slice(prefix.length)
		.split(',')
		.flatMap((rawTerm) => {
			const term = decodeRawValue(rawTerm)?.trim();

			return term ? [[key, `${prefix}${term}`] as [string, string]] : [];
		});
};

// Not `URLSearchParams`: paramount splits on the first `=` and drops only the malformed pair (or term).
const collectRawEntries = (searchString: string): Array<[string, string]> => {
	const trimmed = searchString.startsWith('?') ? searchString.slice(1) : searchString;

	if (!trimmed) {
		return [];
	}

	return trimmed.split('&').flatMap((part): Array<[string, string]> => {
		const eqIndex = part.indexOf('=');
		const key = safeDecodeURIComponent(eqIndex === -1 ? part : part.slice(0, eqIndex));
		const rawValue = eqIndex === -1 ? '' : part.slice(eqIndex + 1);

		if (key === null) {
			return [];
		}

		if (isTermListEntry(key, rawValue)) {
			return expandTermList(key, rawValue);
		}

		const value = decodeRawValue(rawValue);

		return value === null ? [] : [[key, value]];
	});
};

const splitOnComma = (raw: string): string[] =>
	raw
		.split(',')
		.map((part) => part.trim())
		.filter((part) => part.length > 0);

const appendUniqueTo = (target: string[], values: string[]): void => {
	const seen = new Set(target);

	values.forEach((value) => {
		if (!seen.has(value)) {
			target.push(value);
			seen.add(value);
		}
	});
};

const parseBoolean = (raw: string): boolean | null => {
	if (raw === 'true') {
		return true;
	}

	if (raw === 'false') {
		return false;
	}

	return null;
};

const parseRange = (raw: string): { from: string | null; to: string | null } | null => {
	if (!raw.includes('~')) {
		return null;
	}

	const [fromRaw, toRaw] = raw.split('~', 2);
	const from = fromRaw.length > 0 ? fromRaw : null;
	const to = toRaw.length > 0 ? toRaw : null;

	if (from === null && to === null) {
		return null;
	}

	return { from, to };
};

/** Lowercase `rrggbb` for a 3 or 6 digit hex colour (leading `#` optional), else null. */
const normaliseColorHex = (raw: string): string | null => {
	const stripped = raw.startsWith('#') ? raw.slice(1) : raw;

	if (HEX6_CHARS_REGEX.test(stripped)) {
		return stripped.toLowerCase();
	}

	if (HEX3_CHARS_REGEX.test(stripped)) {
		return stripped
			.toLowerCase()
			.split('')
			.map((ch) => ch + ch)
			.join('');
	}

	return null;
};

// ---------------------------------------------------------------------------
//  Metaproperties (`mp_<id or name>=…`)
// ---------------------------------------------------------------------------

const isSelectLikeGroup = (group: UrlMetapropertyGroup, mpId: string): boolean =>
	'mpId' in group && group.mpId === mpId;

const decodeBulkMetapropertyInto = (state: UrlFilters, fieldName: string, value: string): void => {
	const term = value.slice(BULK_SENTINEL_PREFIX.length).trim();

	if (!term) {
		return;
	}

	const existingIndex = state.metaproperties.findIndex(
		(group) => 'fieldName' in group && group.fieldName === fieldName,
	);
	const existing = existingIndex === -1 ? null : state.metaproperties[existingIndex];

	if (!existing || !('bulkTerms' in existing)) {
		state.metaproperties.push({ fieldName, bulkTerms: [term] });

		return;
	}

	state.metaproperties[existingIndex] = {
		fieldName,
		bulkTerms: [...new Set([...existing.bulkTerms, term])].sort(),
	};
};

const decodeMetapropertyInto = (state: UrlFilters, rawKey: string, value: string): void => {
	if (!rawKey) {
		return;
	}

	if (value.startsWith(BULK_SENTINEL_PREFIX)) {
		decodeBulkMetapropertyInto(state, rawKey, value);

		return;
	}

	const mpId = metapropertyIdFromUrl(rawKey);
	const existingIndex = state.metaproperties.findIndex((group) => isSelectLikeGroup(group, mpId));
	const existing = existingIndex === -1 ? null : state.metaproperties[existingIndex];

	if (value === 'exclude') {
		if (!existing) {
			state.metaproperties.push({ mpId, exclude: true });
		}

		return;
	}

	if (value.startsWith(DATE_SENTINEL_PREFIX)) {
		const [from, to] = value.slice(DATE_SENTINEL_PREFIX.length).split('~', 2);

		if (!existing && from && to) {
			state.metaproperties.push({ mpId, dateRange: { from, to } });
		}

		return;
	}

	const optionIds = splitOnComma(value).map(metapropertyIdFromUrl);

	if (optionIds.length === 0) {
		return;
	}

	if (!existing) {
		state.metaproperties.push({ mpId, optionIds });

		return;
	}

	// Repeated `mp_<id>` keys merge; an exclude or date group already there wins.
	if ('optionIds' in existing) {
		state.metaproperties[existingIndex] = {
			mpId,
			optionIds: [...new Set([...existing.optionIds, ...optionIds])].sort(),
		};
	}
};

// ---------------------------------------------------------------------------
//  Reserved keys
// ---------------------------------------------------------------------------

const hasAnyNonStickySearch = (state: UrlFilters): boolean =>
	state.searchTerms.length > 0 ||
	state.titleTerms.length > 0 ||
	state.descriptionTerms.length > 0;

const hasAnyScopedSticky = (state: UrlFilters): boolean =>
	state.stickyTitle !== null || state.stickyDescription !== null;

const hasAnySticky = (state: UrlFilters): boolean =>
	state.stickySearchTerm !== null || hasAnyScopedSticky(state);

const ADVANCED_SINGLE_FIELDS: ReadonlySet<string> = new Set(['type', 'orientation', 'userCreated']);
const ADVANCED_RANGE_FIELDS: ReadonlySet<string> = new Set(Object.keys(RANGE_PRESETS_BY_FIELD));
const ADVANCED_BOOLEAN_FIELDS: ReadonlySet<string> = new Set([
	'archive',
	'watermark',
	'active',
	'downloadable',
	'keyVisual',
	'hasUsage',
	'public',
]);

const decodeReservedKeyInto = (state: UrlFilters, key: string, value: string): void => {
	switch (key) {
		case 'tags':
			appendUniqueTo(state.manualTags, splitOnComma(value));

			return;
		case 'autotags':
			appendUniqueTo(state.autoTags, splitOnComma(value));

			return;
		case 'searchTerm':
		case 'title':
		case 'description': {
			// Sticky and non-sticky terms are mutually exclusive: whichever comes first in the URL wins.
			if (!hasAnySticky(state)) {
				const target = {
					searchTerm: state.searchTerms,
					title: state.titleTerms,
					description: state.descriptionTerms,
				}[key];

				const term = value.trim();

				if (term) {
					appendUniqueTo(target, [term]);
				}
			}

			return;
		}
		case 'stickySearchTerm':
			if (
				!state.stickySearchTerm &&
				!hasAnyNonStickySearch(state) &&
				!hasAnyScopedSticky(state) &&
				value.length > 0
			) {
				state.stickySearchTerm = value;
			}

			return;
		case 'stickyTitle':
		case 'stickyDescription':
			if (
				!state[key] &&
				!hasAnyNonStickySearch(state) &&
				!state.stickySearchTerm &&
				value.length > 0
			) {
				state[key] = value;
			}

			return;
		case 'color': {
			const normalised = normaliseColorHex(value);

			if (normalised) {
				state.color = normalised;
			}

			return;
		}
		default:
			break;
	}

	if (ADVANCED_SINGLE_FIELDS.has(key)) {
		if (value.length > 0) {
			state.advancedSingleFilters.push({ field: key as UrlAdvancedSingleField, value });
		}

		return;
	}

	if (ADVANCED_RANGE_FIELDS.has(key)) {
		const field = key as UrlAdvancedRangeField;

		if (RANGE_PRESETS_BY_FIELD[field].includes(value)) {
			state.advancedRangeFilters.push({ field, preset: value });

			return;
		}

		const range = parseRange(value);

		if (range) {
			state.advancedRangeFilters.push({ field, from: range.from, to: range.to });
		}

		return;
	}

	if (ADVANCED_BOOLEAN_FIELDS.has(key)) {
		const parsed = parseBoolean(value);

		if (parsed !== null) {
			state.advancedBooleanFilters.push({
				field: key as UrlAdvancedBooleanField,
				value: parsed,
			});
		}
	}
};

/**
 * Parses a query string into the filters it carries. Total: never throws, and params it doesn't
 * know or can't parse are dropped, as paramount does.
 */
export const decodeUrlFilters = (searchString: string): UrlFilters => {
	const state: UrlFilters = {
		metaproperties: [],
		manualTags: [],
		autoTags: [],
		searchTerms: [],
		titleTerms: [],
		descriptionTerms: [],
		stickySearchTerm: null,
		stickyTitle: null,
		stickyDescription: null,
		advancedSingleFilters: [],
		advancedRangeFilters: [],
		advancedBooleanFilters: [],
		color: null,
	};

	collectRawEntries(searchString).forEach(([key, value]) => {
		if (key.startsWith(METAPROPERTY_KEY_PREFIX)) {
			decodeMetapropertyInto(state, key.slice(METAPROPERTY_KEY_PREFIX.length), value);

			return;
		}

		decodeReservedKeyInto(state, key, value);
	});

	state.manualTags.sort();
	state.autoTags.sort();
	state.searchTerms.sort();
	state.titleTerms.sort();
	state.descriptionTerms.sort();

	return state;
};

// ---------------------------------------------------------------------------
//  Filter state -> request (`getFiltersParamsFromUrl` + `buildSearchTermQueryClauses`)
// ---------------------------------------------------------------------------

/** Why a decoded filter produced no clause; the caller reports it. */
export type DroppedUrlFilterReason = 'date-preset-today' | 'invalid-date-range';

type OnDropped = (reason: DroppedUrlFilterReason) => void;

const URL_TO_REDUX_RANGE_FIELD: Record<UrlAdvancedRangeField, string> = {
	createdAt: 'dateCreated',
	publishedAt: 'datePublished',
	archivedAt: 'scheduledArchiveDate',
	resolution: 'resolution',
	duration: 'duration',
	dpi: 'dpi',
};

const URL_TO_REDUX_BOOLEAN_FIELD: Record<UrlAdvancedBooleanField, string> = {
	archive: 'archive',
	watermark: 'watermark',
	active: 'isActive',
	downloadable: 'isDownloadable',
	keyVisual: 'keyVisual',
	hasUsage: 'hasUsage',
	public: 'isPublic',
};

// The backend counts these as `1`/`0`; the other booleans are `true`/`false`.
const NUMERIC_BOOLEAN_FIELDS: ReadonlySet<UrlAdvancedBooleanField> = new Set([
	'archive',
	'watermark',
	'keyVisual',
	'hasUsage',
]);

const DATE_RANGE_FIELDS: ReadonlySet<UrlAdvancedRangeField> = new Set([
	'createdAt',
	'publishedAt',
	'archivedAt',
]);

const isValidDate = (value: string): boolean => !Number.isNaN(new Date(value).getTime());

const advancedChip = (fieldName: string, name: string, rangeValue?: string[]): FilteringOption => ({
	type: FilteringOptionType.advancedFilter,
	fieldName,
	name,
	label: name,
	...(rangeValue ? { rangeValue } : {}),
});

const buildSelectClauses = (state: UrlFilters): string[] => {
	const clauses: string[] = [];

	state.metaproperties.forEach((group) => {
		if ('bulkTerms' in group || 'dateRange' in group) {
			return;
		}

		const mpUuid = metapropertyIdToUUID(group.mpId);

		if ('exclude' in group) {
			clauses.push(`metapropertyid_${mpUuid}:NULL`);

			return;
		}

		group.optionIds.forEach((optionId) =>
			clauses.push(`metapropertyid_${mpUuid}:${metapropertyIdToUUID(optionId)}`),
		);
	});

	return clauses;
};

const bulkChips = (state: UrlFilters): FilteringOption[] =>
	state.metaproperties.flatMap((group) =>
		'bulkTerms' in group
			? [
					{
						type: FilteringOptionType.smartFilter,
						fieldName: group.fieldName,
						name: group.bulkTerms.join('\n'),
						isBulkSearch: true,
					},
			  ]
			: [],
	);

// Keyed by id: the index holds the same raw dates under `metafieldid_<uuid>` as under `metafield_<name>`.
const buildDateClauses = (state: UrlFilters, onDropped?: OnDropped): string[] =>
	state.metaproperties.flatMap((group) => {
		if (!('dateRange' in group)) {
			return [];
		}

		if (!isValidDate(group.dateRange.from) || !isValidDate(group.dateRange.to)) {
			onDropped?.('invalid-date-range');

			return [];
		}

		return [
			`metafieldid_${metapropertyIdToUUID(group.mpId)}:${buildDateRangeValue(
				group.dateRange,
			)}`,
		];
	});

const buildAdvancedChips = (state: UrlFilters, onDropped?: OnDropped): FilteringOption[] => {
	const chips: FilteringOption[] = state.advancedSingleFilters.map(({ field, value }) =>
		advancedChip(field, value),
	);

	state.advancedRangeFilters.forEach((entry) => {
		const fieldName = URL_TO_REDUX_RANGE_FIELD[entry.field];

		if (entry.preset !== undefined) {
			// Paramount's URL builder never writes `today`, and its clause builder has no branch for it.
			if (entry.preset === 'today') {
				onDropped?.('date-preset-today');

				return;
			}

			chips.push(advancedChip(fieldName, entry.preset));

			return;
		}

		if (entry.field === 'dpi') {
			if (entry.from) {
				chips.push(advancedChip(fieldName, entry.from));
			}

			return;
		}

		const rangeValue = [entry.from ?? '', entry.to ?? ''];

		// An open or unparseable bound would make `toISOString` throw, as it does in paramount.
		if (DATE_RANGE_FIELDS.has(entry.field) && !rangeValue.every(isValidDate)) {
			onDropped?.('invalid-date-range');

			return;
		}

		chips.push(advancedChip(fieldName, 'custom', rangeValue));
	});

	state.advancedBooleanFilters.forEach(({ field, value }) => {
		const [trueName, falseName] = NUMERIC_BOOLEAN_FIELDS.has(field)
			? ['1', '0']
			: ['true', 'false'];

		chips.push(advancedChip(URL_TO_REDUX_BOOLEAN_FIELD[field], value ? trueName : falseName));
	});

	return chips;
};

const searchTermChip = (
	term: string,
	searchOptions: SelectedSearchOption[] = [],
): FilteringOption => ({
	type: FilteringOptionType.searchTerm,
	fieldName: 'searchTerm',
	name: term,
	searchOptions,
});

const buildSearchTermChips = (state: UrlFilters): FilteringOption[] => {
	const title = [{ key: SearchOptionType.Title }];
	const description = [{ key: SearchOptionType.Description }];

	return [
		...state.searchTerms.map((term) => searchTermChip(term)),
		...state.titleTerms.map((term) => searchTermChip(term, title)),
		...state.descriptionTerms.map((term) => searchTermChip(term, description)),
		...(state.stickySearchTerm !== null ? [searchTermChip(state.stickySearchTerm)] : []),
		...(state.stickyTitle !== null ? [searchTermChip(state.stickyTitle, title)] : []),
		...(state.stickyDescription !== null
			? [searchTermChip(state.stickyDescription, description)]
			: []),
	];
};

/** Same order as paramount's `getFiltersParamsFromUrl`: select, tags, bulk, date, advanced, colour. */
export const mapUrlFiltersToFiltersParam = (
	state: UrlFilters,
	searchOperator: SearchOperator,
	onDropped?: OnDropped,
): FiltersParameterForPostRequest => {
	const tagAndBulkChips: FilteringOption[] = [
		...state.manualTags.map((tag) => ({
			type: FilteringOptionType.tagsFilter,
			fieldName: 'manualTags',
			name: tag,
		})),
		...state.autoTags.map((tag) => ({
			type: FilteringOptionType.tagsFilter,
			fieldName: 'autoTags',
			name: tag,
		})),
		...bulkChips(state),
	];
	const otherChips: FilteringOption[] = [
		...buildAdvancedChips(state, onDropped),
		...(state.color
			? [
					{
						type: FilteringOptionType.colorFilter,
						fieldName: 'color',
						name: `#${state.color}`,
					},
			  ]
			: []),
		...buildSearchTermChips(state),
	];
	// Two calls, so the id-keyed date clauses land between bulk and advanced, as in paramount.
	const tagsAndBulk = mapFilteringOptionsToFiltersParam(tagAndBulkChips, searchOperator);
	const { filters, searchTerms } = mapFilteringOptionsToFiltersParam(otherChips, searchOperator);
	const allFilters = [
		...buildSelectClauses(state),
		...tagsAndBulk.filters,
		...buildDateClauses(state, onDropped),
		...filters,
	];

	return searchTerms ? { filters: allFilters, searchTerms } : { filters: allFilters };
};

/** Convenience: decode the page's query string and map it in a single call. */
export const readFiltersParamFromUrl = (
	searchOperator: SearchOperator,
	{
		search = globalThis.location?.search ?? '',
		onDropped,
	}: { search?: string; onDropped?: OnDropped } = {},
): FiltersParameterForPostRequest =>
	mapUrlFiltersToFiltersParam(decodeUrlFilters(search), searchOperator, onDropped);
